'use client';

import { getCookies } from '@hyphengroup/cms-components/utils/cookies';
import { generateEmailHash } from '@hyphengroup/cms-components/utils/emailHash';
import { useEffect, useState } from 'react';
import { createTrackingParameters } from '../lib/tracking.shared';
import { CmsApplyRewardProduct } from '$/(cms)/_modules/cmsService/types/applyRewardProduct';
import { parseIdentityToken } from '$/modules/auth/shared';

type Props = {
  leadId: string;
  product: CmsApplyRewardProduct;
  stepName: string;
  stepNumber: string;
};

type MetaTag = {
  name: string;
  content: string;
};

export function ApplyTrackingMeta({
  leadId,
  product,
  stepName,
  stepNumber,
}: Props) {
  const [metaTags, setMetaTags] = useState<MetaTag[]>([]);

  useEffect(() => {
    const cookies = getCookies();
    const idToken = cookies.id_token;
    const identity = idToken ? parseIdentityToken(idToken) : undefined;
    const normalizedEmail = identity?.email?.trim().toLocaleLowerCase();
    const trackingParameters = createTrackingParameters(product);
    const tags = [
      { name: 'mhg_cid', content: identity?.sub ?? '' },
      {
        name: 'email_hash',
        content: normalizedEmail
          ? (generateEmailHash(normalizedEmail) ?? '')
          : '',
      },
      { name: 'login_state', content: idToken ? 'true' : 'false' },
      {
        name: 'verification_state',
        content: identity?.email_verified ? 'true' : 'false',
      },
    ];

    if (stepName !== 'lead-submission' || leadId) {
      tags.push(
        {
          name: 'platform_meta',
          content: `fe-services-cms|${process.env.NEXT_PUBLIC_VERSION}`,
        },
        { name: 'page_category', content: 'rewards page' },
        {
          name: 'vertical_code',
          content: trackingParameters.itemId.split('.')[1] ?? '',
        },
        { name: 'item_id', content: trackingParameters.itemId },
        { name: 'item_name', content: trackingParameters.itemName },
        { name: 'item_brand', content: trackingParameters.itemBrand },
        {
          name: 'reward_campaign_name',
          content: trackingParameters.rewardCampaignName,
        },
        {
          name: 'reward_campaign_id',
          content: String(trackingParameters.rewardCampaignId ?? ''),
        },
        { name: 'step_number', content: stepNumber },
        { name: 'step_name', content: stepName },
        { name: 'lead_id', content: leadId },
      );
    }

    if (!product.productId) {
      setMetaTags(tags);
      return;
    }

    const experimentId = `LEAD_CAPTURE_${product.productId}`;
    const variantId = cookies[experimentId];

    if (!variantId) {
      setMetaTags(tags);
      return;
    }

    setMetaTags([
      ...tags,
      { name: 'experiment_id', content: experimentId },
      { name: 'variant_id', content: variantId },
    ]);
  }, [leadId, product, stepName, stepNumber]);

  return metaTags.map((metaTag) => (
    <meta content={metaTag.content} key={metaTag.name} name={metaTag.name} />
  ));
}
